import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, CheckCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/hooks/useAuth";
import {AutoTranslate} from "@/lib/autoTranslate.tsx"


export default function ForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;

    const { error } = await resetPassword(email);

    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
    }

    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-main p-6">
        {/* Background Pattern */}
        <div
          className="fixed inset-0 opacity-30 pointer-events-none dark:opacity-10"
          style={{
            backgroundImage: `var(--grid-pattern)`,
          }}
        />

        <div className="relative z-10 w-full max-w-md">
          <Card className="bg-card/90 backdrop-blur-sm border-card-border">
            <CardHeader className="text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <CardTitle>Check Your Email</CardTitle>
              <CardDescription>
                We've sent a password reset link to your email address.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button onClick={() => navigate('/auth')} className="w-full">
                {AutoTranslate(`Back to Sign In`)}
              </Button>`
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-main p-6">
      {/* Background Pattern */}
      <div
        className="fixed inset-0 opacity-30 pointer-events-none dark:opacity-10"
        style={{
          backgroundImage: `var(--grid-pattern)`,
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">F I N D</h1>
          <p className="text-muted-foreground">{AutoTranslate(`Resume Builder`)}</p>
        </div>

        <Card className="bg-card/90 backdrop-blur-sm border-card-border">
          <CardHeader className="text-center">
            <CardTitle>{AutoTranslate(`Reset Password`)}</CardTitle>
            <CardDescription>
              {AutoTranslate(`Enter your email address and we'll send you a link to reset your password.`)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{AutoTranslate(`Email`)}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your@email.com"
                  required
                  className="bg-input border-input-border"
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
            </form>

            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="text-center mt-4">
              <Button
                variant="link"
                onClick={() => navigate('/auth')}
                className="text-muted-foreground"
              >
                ← {AutoTranslate(`Back to Sign In`)}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}